import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  dens as densApi,
  RANK_LABELS,
  RANK_ORDER,
  units as unitsApi,
  type DenRow,
  type DenLeaderRow,
  type Member,
  type RolloverPreview,
  type RolloverResult,
} from '../api';
import { useUnits } from '../units';

export function Dens() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole, reload: reloadUnits } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER');
  const isAdmin = hasRole('ADMIN');

  const [dens, setDens] = useState<DenRow[] | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const load = useCallback(() => {
    setError(null);
    densApi
      .list(unitId)
      .then((r) => setDens(r.dens))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load'));
  }, [unitId]);

  useEffect(() => {
    if (!mayView) return;
    load();
    if (isAdmin) unitsApi.members(unitId).then(setMembers).catch(() => setMembers([]));
  }, [mayView, isAdmin, load, unitId]);

  if (!mayView) return <Navigate to="/" replace />;

  async function addDen(e: FormEvent) {
    e.preventDefault();
    try {
      await densApi.create(unitId, newName);
      setNewName('');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add den');
    }
  }

  return (
    <div className="panel">
      <h1>Dens</h1>
      <p className="muted">{currentUnit?.name}</p>
      {error && <p className="error">{error}</p>}

      {isAdmin && (
        <form className="add-member" onSubmit={addDen}>
          <input
            placeholder="Den name (e.g. Wolves)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <button type="submit" className="primary">
            Add den
          </button>
        </form>
      )}

      <div className="den-cards">
        {dens?.map((d) => (
          <DenCard
            key={d.id}
            unitId={unitId}
            den={d}
            isAdmin={isAdmin}
            members={members}
            onChanged={load}
          />
        ))}
        {dens?.length === 0 && <p className="muted">No dens yet.</p>}
      </div>

      {isAdmin && (dens?.length ?? 0) > 0 && (
        <Rollover
          unitId={unitId}
          dens={dens ?? []}
          onDone={() => {
            load();
            void reloadUnits();
          }}
        />
      )}
    </div>
  );
}

const GRADUATING = '__graduating__';

function Rollover({
  unitId,
  dens,
  onDone,
}: {
  unitId: string;
  dens: DenRow[];
  onDone: () => void;
}) {
  const [preview, setPreview] = useState<RolloverPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<RolloverResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const p = await densApi.rolloverPreview(unitId);
      setPreview(p);
      const m: Record<string, string> = {};
      for (const d of p.dens) {
        if (d.scoutCount === 0) continue;
        m[d.denId] = d.graduating ? GRADUATING : (d.suggestedTargetDenId ?? '');
      }
      setMapping(m);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not build the preview');
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    if (!preview) return;
    setError(null);
    setBusy(true);
    try {
      const map: Record<string, string | null> = {};
      for (const [denId, v] of Object.entries(mapping)) map[denId] = v === GRADUATING ? null : v;
      const r = await densApi.rolloverCommit(unitId, { toYear: preview.toYear, mapping: map });
      setResult(r);
      setPreview(null);
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Rollover failed');
    } finally {
      setBusy(false);
    }
  }

  const rows = (preview?.dens ?? []).filter((d) => d.scoutCount > 0);
  const ready = rows.every((d) => mapping[d.denId]);

  return (
    <div className="callout" style={{ marginTop: 20 }}>
      <h2 style={{ marginTop: 0 }}>Program-year rollover</h2>
      {error && <p className="error">{error}</p>}
      {result && (
        <p className="muted">
          Rolled up to program year <strong>{result.toYear}</strong>: {result.moved} moved,{' '}
          {result.stayed} stayed, {result.graduated} graduated.
        </p>
      )}

      {!preview ? (
        <button onClick={start} disabled={busy}>
          Roll Scouts up a den…
        </button>
      ) : (
        <>
          <p className="muted small">
            Moving from {preview.fromYear} to <strong>{preview.toYear}</strong>. Prior-year den
            history is kept. Pick where each den’s Scouts go.
          </p>
          <table className="grid">
            <thead>
              <tr>
                <th>Den ({preview.fromYear})</th>
                <th>Scouts</th>
                <th>→ Den ({preview.toYear})</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.denId}>
                  <td>
                    {d.denName}
                    {d.rank && <span className="muted small"> · {RANK_LABELS[d.rank] ?? d.rank}</span>}
                  </td>
                  <td>{d.scoutCount}</td>
                  <td>
                    <select
                      value={mapping[d.denId] ?? ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [d.denId]: e.target.value }))}
                    >
                      <option value="">— choose —</option>
                      {dens.map((den) => (
                        <option key={den.id} value={den.id}>
                          {den.id === d.denId ? `${den.name} (stay)` : den.name}
                        </option>
                      ))}
                      <option value={GRADUATING}>Graduating / leaving</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button
              className="primary"
              disabled={busy || !ready}
              onClick={() => {
                if (confirm(`Roll ${preview.totalScouts} Scout(s) into program year ${preview.toYear}?`)) commit();
              }}
            >
              Commit rollover
            </button>
            <button onClick={() => setPreview(null)}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

function DenCard({
  unitId,
  den,
  isAdmin,
  members,
  onChanged,
}: {
  unitId: string;
  den: DenRow;
  isAdmin: boolean;
  members: Member[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [leaders, setLeaders] = useState<DenLeaderRow[] | null>(null);
  const [pick, setPick] = useState('');
  const [name, setName] = useState(den.name);
  const [error, setError] = useState<string | null>(null);

  async function loadLeaders() {
    setLeaders(await densApi.leaders(unitId, den.id));
  }

  function toggle() {
    setOpen((o) => {
      if (!o && leaders === null) void loadLeaders();
      return !o;
    });
  }

  return (
    <div className="den-card">
      <div className="den-card-head">
        <strong>{den.name}</strong>
        <span className="muted small">
          {den.rank ? RANK_LABELS[den.rank] + ' · ' : ''}
          {den.scoutCount} scout{den.scoutCount === 1 ? '' : 's'} · {den.leaderCount} leader
          {den.leaderCount === 1 ? '' : 's'}
        </span>
        <button onClick={toggle}>{open ? 'Close' : 'Manage'}</button>
      </div>

      {open && (
        <div className="den-card-body">
          {error && <p className="error">{error}</p>}
          {isAdmin && (
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={name} onChange={(e) => setName(e.target.value)} />
              <select
                value={den.rank ?? ''}
                onChange={async (e) => {
                  try {
                    await densApi.patch(unitId, den.id, {
                      rank: (e.target.value || null) as never,
                    });
                    onChanged();
                  } catch (err) {
                    setError(err instanceof ApiError ? err.message : 'Could not set rank');
                  }
                }}
              >
                <option value="">No rank</option>
                {RANK_ORDER.map((r) => (
                  <option key={r} value={r}>
                    {RANK_LABELS[r]}
                  </option>
                ))}
              </select>
              <button
                disabled={name.trim() === den.name}
                onClick={async () => {
                  try {
                    await densApi.patch(unitId, den.id, { name });
                    onChanged();
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : 'Rename failed');
                  }
                }}
              >
                Rename
              </button>
              <button
                onClick={async () => {
                  if (!confirm(`Delete den "${den.name}"?`)) return;
                  try {
                    await densApi.remove(unitId, den.id);
                    onChanged();
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : 'Delete failed');
                  }
                }}
              >
                Delete
              </button>
            </div>
          )}

          <h3>Leaders</h3>
          <ul className="units">
            {leaders?.map((l) => (
              <li key={l.userId}>
                <span>{l.displayName} · {l.email}</span>
                {isAdmin && (
                  <button
                    onClick={async () => {
                      await densApi.removeLeader(unitId, den.id, l.userId);
                      await loadLeaders();
                      onChanged();
                    }}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
            {leaders?.length === 0 && <li className="muted">No leaders assigned.</li>}
          </ul>

          {isAdmin && (
            <div className="row" style={{ gap: 8 }}>
              <select value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">Add a leader…</option>
                {members
                  .filter((m) => !leaders?.some((l) => l.userId === m.userId))
                  .map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.displayName} ({m.email})
                    </option>
                  ))}
              </select>
              <button
                disabled={!pick}
                className="primary"
                onClick={async () => {
                  try {
                    setLeaders(await densApi.addLeader(unitId, den.id, pick));
                    setPick('');
                    onChanged();
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : 'Could not add leader');
                  }
                }}
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
